<template>
    <div class="calendar-page">
        <!-- Filters and Controls -->
        <div class="calendar-toolbar mb-3">
            <div class="row align-items-center">
                <div class="col-auto">
                    <div class="btn-group">
                        <button 
                            class="btn btn-sm"
                            :class="viewMode === 'month' ? 'btn-outline-primary active' : 'btn-outline-primary'"
                            @click="viewMode = 'month'"
                        >
                            <i class="fas fa-calendar-alt me-1"></i> Monthly
                        </button>
                        <button 
                            class="btn btn-sm"
                            :class="viewMode === 'week' ? 'btn-outline-primary active' : 'btn-outline-primary'"
                            @click="viewMode = 'week'"
                        >
                            <i class="fas fa-calendar-week me-1"></i> Weekly
                        </button>
                        <button 
                            class="btn btn-sm"
                            :class="viewMode === 'day' ? 'btn-outline-primary active' : 'btn-outline-primary'"
                            @click="viewMode = 'day'"
                        >
                            <i class="fas fa-calendar-day me-1"></i> Daily
                        </button>
                    </div>
                </div>
                
                <div class="col-auto">
                    <div class="btn-group">
                        <button class="btn btn-sm btn-outline-secondary" @click="previousPeriod">
                            <i class="fas fa-chevron-left"></i>
                        </button>
                        <button class="btn btn-sm btn-outline-secondary" @click="goToToday">
                            Today
                        </button>
                        <button class="btn btn-sm btn-outline-secondary" @click="nextPeriod">
                            <i class="fas fa-chevron-right"></i>
                        </button>
                    </div>
                </div>
                
                <div class="col">
                    <h4 class="mb-0">{{ currentPeriodLabel }}</h4>
                </div>
                
                <div class="col-auto">
                    <kt-autoselect
                        label="Task Type"
                        :options="timeTypeOptions"
                        display-by="name"
                        value-by="id"
                        v-model="filters.timeType"
                        @change="loadTasks"
                    ></kt-autoselect>
                </div>
                
                <div class="col-auto">
                    <tech-multi-check 
                        label="Technicians"
                        :model-value="filters.technician"
                        @update:model-value="(value: any) => { filters.technician = value; loadTasks(); }"
                    ></tech-multi-check>
                </div>
                
                <div class="col-auto">
                    <button 
                        class="btn btn-sm btn-outline-primary"
                        @click="openTaskModal(null)"
                    >
                        <i class="fas fa-plus me-1"></i>
                        {{ selectedTask ? 'Edit Task' : 'New Task' }}
                    </button>
                </div>
            </div>
        </div>
        
        <!-- Calendar Views -->
        <div class="calendar-container">
            <!-- Month View -->
            <div v-if="viewMode === 'month'" class="calendar-month">
                <div class="calendar-header">
                    <div class="calendar-day-header" v-for="day in weekDays" :key="day">
                        {{ day }}
                    </div>
                </div>
                <div class="calendar-grid">
                    <div 
                        v-for="(day, index) in monthDays" 
                        :key="index"
                        class="calendar-day"
                        :class="{ 
                            'other-month': !day.isCurrentMonth,
                            'today': day.isToday,
                            'weekend': day.isWeekend
                        }"
                        @dragover.prevent
                        @drop="handleDrop($event, day.date)"
                    >
                        <div class="day-number">{{ day.dayNumber }}</div>
                        <div class="day-tasks">
                            <div 
                                v-for="task in getTasksForDay(day.date)" 
                                :key="String(task.Id || Math.random())"
                                class="task-item"
                                :class="getTaskClass(task)"
                                draggable="true"
                                @dragstart="handleDragStart($event, task)"
                                @click="selectTask(task)"
                            >
                                <div class="task-time" v-if="task.CompleteByDate || task.WorkCompleteDate">
                                    {{ formatTime(task.CompleteByDate || task.WorkCompleteDate) }}
                                </div>
                                <div class="task-title">{{ getTaskTitle(task) }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Week View -->
            <div v-if="viewMode === 'week'" class="calendar-week">
                <div class="week-header">
                    <div class="time-column-header">Time</div>
                    <div 
                        v-for="day in weekDays" 
                        :key="day" 
                        class="week-day-header"
                        :class="{ 'today': isToday(weekDates[weekDays.indexOf(day)]) }"
                    >
                        <div class="day-name">{{ day }}</div>
                        <div class="day-date">{{ formatDate(weekDates[weekDays.indexOf(day)]) }}</div>
                    </div>
                </div>
                <div class="week-grid">
                    <div class="time-column">
                        <div v-for="hour in 24" :key="hour" class="time-slot">
                            {{ formatHour(hour - 1) }}
                        </div>
                    </div>
                    <div 
                        v-for="(date, dayIndex) in weekDates" 
                        :key="dayIndex"
                        class="week-day-column"
                        :class="{ 'today': isToday(date) }"
                    >
                        <div 
                            v-for="hour in 24" 
                            :key="hour"
                            class="week-time-slot"
                            @dragover.prevent
                            @drop="handleDropTime($event, date, hour - 1)"
                        >
                            <div 
                                v-for="task in getTasksForDayHour(date, hour - 1)" 
                                :key="String(task.Id || Math.random())"
                                class="task-item-week"
                                :class="getTaskClass(task)"
                                draggable="true"
                                @dragstart="handleDragStart($event, task)"
                                @click="selectTask(task)"
                            >
                                <div class="task-title-week">{{ getTaskTitle(task) }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Day View -->
            <div v-if="viewMode === 'day'" class="calendar-day-view">
                <div class="day-view-header">
                    <h5>{{ formatDayHeader(currentDate) }}</h5>
                </div>
                <div class="day-view-grid">
                    <div class="time-column">
                        <div v-for="hour in 24" :key="hour" class="time-slot">
                            {{ formatHour(hour - 1) }}
                        </div>
                    </div>
                    <div class="day-view-column">
                        <div 
                            v-for="hour in 24" 
                            :key="hour"
                            class="day-time-slot"
                            @dragover.prevent
                            @drop="handleDropTime($event, currentDate, hour - 1)"
                        >
                            <div 
                                v-for="task in getTasksForDayHour(currentDate, hour - 1)" 
                                :key="String(task.Id || Math.random())"
                                class="task-item-day"
                                :class="getTaskClass(task)"
                                draggable="true"
                                @dragstart="handleDragStart($event, task)"
                                @click="selectTask(task)"
                            >
                                <div class="task-time-day">{{ formatTime(task.CompleteByDate || task.WorkCompleteDate) }}</div>
                                <div class="task-title-day">{{ getTaskTitle(task) }}</div>
                                <div class="task-tech" v-if="task.Technician">{{ task.Technician.TechnicianName }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Task Edit Modal -->
        <time-edit-embedded 
            v-if="showTaskModal && editingTaskId" 
            :record-id="editingTaskId"
            @close="closeTaskModal"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import Api from '@/services/ApiService';
import { TimeModel } from '@/models/TaskModel';
import KtAutoselect from '@/components/UI/KtAutoselect.vue';
import TechMultiCheck from '@/components/Techs/TechMultiCheck.vue';
import TimeEditEmbedded from '@/components/Time/TimeEditEmbedded.vue';
import dayjs from 'dayjs';

// Data
const route = useRoute();
const viewMode = ref<'month' | 'week' | 'day'>('month');
const currentDate = ref(new Date());
const tasks = ref<TimeModel[]>([]);
const selectedTask = ref<TimeModel | null>(null);
const draggedTask = ref<TimeModel | null>(null);
const showTaskModal = ref(false);
const editingTaskId = ref<string | null>(null);

const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const timeTypeOptions = ref([
    { id: 'All', name: 'All' },
    { id: 'Assignments', name: 'Assignments' },
    { id: 'Work', name: 'Work' }
]);

const filters = ref({
    timeType: 'All',
    technician: [] as number[]
});

// Computed
const currentPeriodLabel = computed(() => {
    if (viewMode.value === 'month') {
        return dayjs(currentDate.value).format('MMMM YYYY');
    } else if (viewMode.value === 'week') {
        const start = weekDates.value[0];
        const end = weekDates.value[6];
        return `${dayjs(start).format('MMM D')} - ${dayjs(end).format('MMM D, YYYY')}`;
    } else {
        return dayjs(currentDate.value).format('dddd, MMMM D, YYYY');
    }
});

const monthDays = computed(() => {
    const year = currentDate.value.getUTCFullYear();
    const month = currentDate.value.getUTCMonth();
    
    // Create dates in UTC to match task dates
    const firstDay = new Date(Date.UTC(year, month, 1));
    const lastDay = new Date(Date.UTC(year, month + 1, 0));
    const prevLastDay = new Date(Date.UTC(year, month, 0));
    
    const firstDayOfWeek = firstDay.getUTCDay();
    const daysInMonth = lastDay.getUTCDate();
    const daysInPrevMonth = prevLastDay.getUTCDate();
    
    const days = [];
    
    // Previous month days
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
        const day = daysInPrevMonth - i;
        const date = new Date(Date.UTC(year, month - 1, day));
        days.push({
            dayNumber: day,
            date: date,
            isCurrentMonth: false,
            isToday: false,
            isWeekend: date.getUTCDay() === 0 || date.getUTCDay() === 6
        });
    }
    
    // Current month days
    const today = new Date();
    for (let i = 1; i <= daysInMonth; i++) {
        const date = new Date(Date.UTC(year, month, i));
        const isTodayUTC = date.getUTCFullYear() === today.getUTCFullYear() &&
                           date.getUTCMonth() === today.getUTCMonth() &&
                           date.getUTCDate() === today.getUTCDate();
        days.push({
            dayNumber: i,
            date: date,
            isCurrentMonth: true,
            isToday: isTodayUTC,
            isWeekend: date.getUTCDay() === 0 || date.getUTCDay() === 6
        });
    }
    
    // Next month days
    const remainingDays = 42 - days.length; // 6 weeks * 7 days
    for (let i = 1; i <= remainingDays; i++) {
        const date = new Date(Date.UTC(year, month + 1, i));
        days.push({
            dayNumber: i,
            date: date,
            isCurrentMonth: false,
            isToday: false,
            isWeekend: date.getUTCDay() === 0 || date.getUTCDay() === 6
        });
    }
    
    return days;
});

const weekDates = computed(() => {
    const dates = [];
    // Create start of week in UTC
    const year = currentDate.value.getUTCFullYear();
    const month = currentDate.value.getUTCMonth();
    const day = currentDate.value.getUTCDate();
    const startOfWeek = new Date(Date.UTC(year, month, day));
    const dayOfWeek = startOfWeek.getUTCDay();
    startOfWeek.setUTCDate(startOfWeek.getUTCDate() - dayOfWeek);
    
    for (let i = 0; i < 7; i++) {
        const date = new Date(Date.UTC(
            startOfWeek.getUTCFullYear(),
            startOfWeek.getUTCMonth(),
            startOfWeek.getUTCDate() + i
        ));
        dates.push(date);
    }
    
    return dates;
});

// Methods
const loadTasks = async () => {
    try {
        let startDate: Date;
        let endDate: Date;
        
        if (viewMode.value === 'month') {
            const year = currentDate.value.getFullYear();
            const month = currentDate.value.getMonth();
            startDate = new Date(year, month, 1);
            startDate.setDate(startDate.getDate() - startDate.getDay()); // Start from Sunday before month
            endDate = new Date(year, month + 1, 0);
            endDate.setDate(endDate.getDate() + (6 - endDate.getDay())); // End on Saturday after month
        } else if (viewMode.value === 'week') {
            startDate = weekDates.value[0];
            endDate = weekDates.value[6];
        } else {
            startDate = new Date(currentDate.value);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(currentDate.value);
            endDate.setHours(23, 59, 59, 999);
        }
        
        const params: any = {
            StartDate: startDate,
            EndDate: endDate,
            IncludeRequest: true,
            IncludeContact: true,
            IncludeTechnician: true,
            Size: 1000
        };
        
        if (filters.value.timeType !== 'All') {
            params.TimeType = filters.value.timeType;
        }
        
        if (filters.value.technician.length > 0) {
            params.TechId = filters.value.technician;
        }
        
        const response = await Api.loadTimes(params, false);
        tasks.value = response.Data || [];
    } catch (error) {
        console.error('Failed to load tasks:', error);
        tasks.value = [];
    }
};

const getTasksForDay = (date: Date) => {
    return tasks.value.filter(task => {
        const taskDate = task.CompleteByDate || task.WorkCompleteDate;
        if (!taskDate) return false;
        const taskDay = new Date(taskDate);
        // Compare UTC dates only (ignore time and timezone)
        return taskDay.getUTCFullYear() === date.getUTCFullYear() &&
               taskDay.getUTCMonth() === date.getUTCMonth() &&
               taskDay.getUTCDate() === date.getUTCDate();
    });
};

const getTasksForDayHour = (date: Date, hour: number) => {
    return tasks.value.filter(task => {
        const taskDate = task.CompleteByDate || task.WorkCompleteDate;
        if (!taskDate) return false;
        const taskDay = new Date(taskDate);
        // Compare UTC date and hour
        return taskDay.getUTCFullYear() === date.getUTCFullYear() &&
               taskDay.getUTCMonth() === date.getUTCMonth() &&
               taskDay.getUTCDate() === date.getUTCDate() &&
               taskDay.getUTCHours() === hour;
    });
};

const getTaskTitle = (task: TimeModel) => {
    if (task.WorkAssignment) return task.WorkAssignment;
    if (task.ActualWork) return task.ActualWork;
    if (task.InvoiceText) return task.InvoiceText;
    return `Task #${task.Id}`;
};

const getTaskClass = (task: TimeModel) => {
    const classes = [];
    
    // Assignment vs Work
    if (task.WorkAssignment && !task.ActualWork) {
        classes.push('task-assignment');
    } else {
        classes.push('task-work');
    }
    
    // Status-based coloring
    if (task.Status) {
        classes.push(`task-status-${task.Status}`);
    }
    
    if (selectedTask.value?.Id === task.Id) {
        classes.push('task-selected');
    }
    
    return classes.join(' ');
};

const formatTime = (dateStr: string | Date | null) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return dayjs(date).format('h:mm A');
};

const formatDate = (date: Date) => {
    return dayjs(date).format('M/D');
};

const formatHour = (hour: number) => {
    return dayjs().hour(hour).minute(0).format('h A');
};

const formatDayHeader = (date: Date) => {
    return dayjs(date).format('dddd, MMMM D, YYYY');
};

const isToday = (date: Date) => {
    const today = new Date();
    // Compare UTC dates to avoid timezone issues
    return date.getUTCFullYear() === today.getUTCFullYear() &&
           date.getUTCMonth() === today.getUTCMonth() &&
           date.getUTCDate() === today.getUTCDate();
};

const previousPeriod = () => {
    if (viewMode.value === 'month') {
        const year = currentDate.value.getUTCFullYear();
        const month = currentDate.value.getUTCMonth();
        currentDate.value = new Date(Date.UTC(year, month - 1, 1));
    } else if (viewMode.value === 'week') {
        currentDate.value = new Date(currentDate.value.getTime() - 7 * 24 * 60 * 60 * 1000);
    } else {
        currentDate.value = new Date(currentDate.value.getTime() - 24 * 60 * 60 * 1000);
    }
    loadTasks();
};

const nextPeriod = () => {
    if (viewMode.value === 'month') {
        const year = currentDate.value.getUTCFullYear();
        const month = currentDate.value.getUTCMonth();
        currentDate.value = new Date(Date.UTC(year, month + 1, 1));
    } else if (viewMode.value === 'week') {
        currentDate.value = new Date(currentDate.value.getTime() + 7 * 24 * 60 * 60 * 1000);
    } else {
        currentDate.value = new Date(currentDate.value.getTime() + 24 * 60 * 60 * 1000);
    }
    loadTasks();
};

const goToToday = () => {
    currentDate.value = new Date();
    loadTasks();
};

const selectTask = (task: TimeModel) => {
    selectedTask.value = task;
};

const openTaskModal = (task: TimeModel | null) => {
    if (task || selectedTask.value) {
        editingTaskId.value = (task || selectedTask.value)?.Id?.toString() || null;
    } else {
        editingTaskId.value = 'new';
    }
    showTaskModal.value = true;
};

const closeTaskModal = () => {
    showTaskModal.value = false;
    editingTaskId.value = null;
    selectedTask.value = null;
    loadTasks();
};

const handleDragStart = (event: DragEvent, task: TimeModel) => {
    draggedTask.value = task;
    event.dataTransfer!.effectAllowed = 'move';
};

const handleDrop = async (event: DragEvent, date: Date) => {
    event.preventDefault();
    console.log('handleDrop called with date:', date, 'draggedTask:', draggedTask.value);
    
    if (!draggedTask.value) {
        console.log('No dragged task, returning');
        return;
    }
    
    try {
        const task = draggedTask.value;
        
        // Validate date
        if (!date || isNaN(date.getTime())) {
            console.error('Invalid date passed to handleDrop:', date, 'type:', typeof date);
            return;
        }
        
        // Create date in UTC to avoid timezone shifts
        console.log('Target date:', date, 'UTC components:', date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
        const newDate = new Date(Date.UTC(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate(),
            12, 0, 0, 0 // Use noon UTC to avoid day-boundary issues
        ));
        console.log('Created newDate (noon UTC):', newDate, newDate.toISOString());
        
        // Preserve time if it exists
        if (task.CompleteByDate || task.WorkCompleteDate) {
            const oldDate = new Date(task.CompleteByDate || task.WorkCompleteDate || '');
            console.log('Old date:', task.CompleteByDate || task.WorkCompleteDate, 'parsed as:', oldDate);
            if (!isNaN(oldDate.getTime())) {
                console.log('Preserving time - old UTC hours:', oldDate.getUTCHours(), 'minutes:', oldDate.getUTCMinutes());
                newDate.setUTCHours(oldDate.getUTCHours(), oldDate.getUTCMinutes(), 0, 0);
                console.log('After preserving time:', newDate, newDate.toISOString());
            }
        }
        
        // Validate newDate before saving
        if (isNaN(newDate.getTime())) {
            console.error('Invalid newDate created:', newDate);
            return;
        }
        
        // Format date as simple YYYY-MM-DD string
        // Helper.dateFromString will add time component to ensure local interpretation
        const year = newDate.getUTCFullYear();
        const month = String(newDate.getUTCMonth() + 1).padStart(2, '0');
        const day = String(newDate.getUTCDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;
        
        const updatedTask = {
            ...task,
            CompleteByDate: task.WorkAssignment ? dateString : task.CompleteByDate,
            WorkCompleteDate: task.ActualWork ? dateString : task.WorkCompleteDate
        };
        
        console.log('Saving task with date:', dateString, 'from UTC date:', newDate, updatedTask);
        await Api.saveTime(updatedTask);
        await loadTasks();
        draggedTask.value = null;
    } catch (error) {
        console.error('Failed to update task:', error);
        draggedTask.value = null;
    }
};

const handleDropTime = async (event: DragEvent, date: Date, hour: number) => {
    event.preventDefault();
    if (!draggedTask.value) return;
    
    try {
        const task = draggedTask.value;
        // Create date in UTC with specified hour
        const newDate = new Date(Date.UTC(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate(),
            hour, 0, 0, 0
        ));
        
        // Format date as simple YYYY-MM-DD string
        // Helper.dateFromString will handle adding time for local interpretation
        const year = newDate.getUTCFullYear();
        const month = String(newDate.getUTCMonth() + 1).padStart(2, '0');
        const day = String(newDate.getUTCDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;
        
        const updatedTask = {
            ...task,
            CompleteByDate: task.WorkAssignment ? dateString : task.CompleteByDate,
            WorkCompleteDate: task.ActualWork ? dateString : task.WorkCompleteDate
        };
        
        await Api.saveTime(updatedTask);
        await loadTasks();
        draggedTask.value = null;
    } catch (error) {
        console.error('Failed to update task:', error);
        draggedTask.value = null;
    }
};

// Watchers
watch(viewMode, () => {
    loadTasks();
});

watch(() => route.params.filter, () => {
    loadTasks();
}, { immediate: false });

// Lifecycle
onMounted(() => {
    loadTasks();
});
</script>

<style scoped>
.calendar-page {
    padding: 1rem;
    height: calc(100vh - 100px);
    display: flex;
    flex-direction: column;
}

.calendar-toolbar {
    flex-shrink: 0;
}

.calendar-container {
    flex: 1;
    overflow: auto;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* Month View */
.calendar-month {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.calendar-header {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    border-bottom: 2px solid #dee2e6;
    background: #f8f9fa;
}

.calendar-day-header {
    padding: 0.75rem;
    text-align: center;
    font-weight: 600;
    border-right: 1px solid #dee2e6;
}

.calendar-day-header:last-child {
    border-right: none;
}

.calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    grid-template-rows: repeat(6, 1fr);
    flex: 1;
}

.calendar-day {
    border: 1px solid #dee2e6;
    padding: 0.5rem;
    min-height: 100px;
    background: white;
    cursor: pointer;
    overflow: auto;
}

.calendar-day:hover {
    background: #f8f9fa;
}

.calendar-day.other-month {
    background: #f1f3f5;
    color: #adb5bd;
}

.calendar-day.today {
    background: #e7f5ff;
    border-color: #0d6efd;
}

.calendar-day.weekend {
    background: #f8f9fa;
}

.day-number {
    font-weight: 600;
    margin-bottom: 0.25rem;
    font-size: 0.875rem;
}

.day-tasks {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.task-item {
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    cursor: move;
    border-left: 3px solid;
}

.task-item:hover {
    opacity: 0.8;
}

.task-assignment {
    background: #e8f4f8;
    border-left-color: #0dcaf0;
}

.task-work {
    background: #d1e7dd;
    border-left-color: #198754;
}

.task-selected {
    box-shadow: 0 0 0 2px #0d6efd;
}

.task-time {
    font-weight: 600;
    color: #495057;
}

.task-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Week View */
.calendar-week {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.week-header {
    display: grid;
    grid-template-columns: 80px repeat(7, 1fr);
    border-bottom: 2px solid #dee2e6;
    background: #f8f9fa;
}

.time-column-header {
    padding: 0.75rem;
    text-align: center;
    font-weight: 600;
    border-right: 1px solid #dee2e6;
}

.week-day-header {
    padding: 0.75rem;
    text-align: center;
    border-right: 1px solid #dee2e6;
}

.week-day-header.today {
    background: #e7f5ff;
    font-weight: 600;
}

.day-name {
    font-weight: 600;
}

.day-date {
    font-size: 0.875rem;
    color: #6c757d;
}

.week-grid {
    display: grid;
    grid-template-columns: 80px repeat(7, 1fr);
    flex: 1;
    overflow: auto;
}

.time-column {
    border-right: 2px solid #dee2e6;
    background: #f8f9fa;
}

.time-slot {
    padding: 0.5rem;
    height: 60px;
    border-bottom: 1px solid #dee2e6;
    font-size: 0.75rem;
    text-align: right;
}

.week-day-column {
    border-right: 1px solid #dee2e6;
}

.week-day-column.today {
    background: #f8f9ff;
}

.week-time-slot {
    height: 60px;
    border-bottom: 1px solid #e9ecef;
    position: relative;
    padding: 0.25rem;
}

.week-time-slot:hover {
    background: #f8f9fa;
}

.task-item-week {
    padding: 0.25rem;
    border-radius: 4px;
    font-size: 0.7rem;
    cursor: move;
    border-left: 3px solid;
    margin-bottom: 0.125rem;
}

.task-title-week {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Day View */
.calendar-day-view {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.day-view-header {
    padding: 1rem;
    border-bottom: 2px solid #dee2e6;
    background: #f8f9fa;
    text-align: center;
}

.day-view-grid {
    display: grid;
    grid-template-columns: 80px 1fr;
    flex: 1;
    overflow: auto;
}

.day-view-column {
    border-left: 2px solid #dee2e6;
}

.day-time-slot {
    height: 80px;
    border-bottom: 1px solid #e9ecef;
    position: relative;
    padding: 0.5rem;
}

.day-time-slot:hover {
    background: #f8f9fa;
}

.task-item-day {
    padding: 0.5rem;
    border-radius: 4px;
    font-size: 0.875rem;
    cursor: move;
    border-left: 4px solid;
    margin-bottom: 0.25rem;
}

.task-time-day {
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.25rem;
}

.task-title-day {
    margin-bottom: 0.25rem;
}

.task-tech {
    font-size: 0.75rem;
    color: #6c757d;
}
</style>
